import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/common/Logo';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import toast from 'react-hot-toast';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  const role = 'ADMIN'; // Fixed role for admin login

  const adminConfig = {
    title: 'Admin Dashboard',
    subtitle: 'Manage your platform',
    gradient: 'linear-gradient(135deg, #0F172A, #1E293B)',
  };

  useEffect(() => {
    // Load saved credentials if remember me was checked
    const savedCredentials = localStorage.getItem(`remember_${role}`);
    if (savedCredentials) {
      const { email, password } = JSON.parse(savedCredentials);
      setValue('email', email);
      setValue('password', password);
      setRememberMe(true);
    }
  }, [role, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const result = await login({ ...data, role });
      
      if (result.success) {
        // Save credentials if remember me is checked
        if (rememberMe) {
          localStorage.setItem(`remember_${role}`, JSON.stringify({
            email: data.email,
            password: data.password

          }));
        } else {
          localStorage.removeItem(`remember_${role}`);
        }

        toast.success('Login successful!');
        
        // Redirect to admin dashboard
        navigate('/admin');
      }
      else{
          toast.error(result.error);
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password', { state: { role } });
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(150deg, #0F172A, #1E293B)'
      }}
    >
      <div 
        style={{
          width: '100%',
          maxWidth: '448px',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <Logo />
          </div>
          <h2 
            className="outfit"
            style={{ 
              fontWeight: '700', 
              fontSize: '24px', 
              marginBottom: '8px',
              color: '#0F172A'
            }}
          >
            {adminConfig.title}
          </h2>
          <p 
            style={{ 
              fontSize: '14px',
              fontWeight: '500',
              color: '#64748B'
            }}
          >
            {adminConfig.subtitle}
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Email or Username</label>
            <input
              {...register('email', { 
                required: 'Email or username is required' 
              })}
              type="text"
              placeholder="admin@example.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid #D1D5DB',
                background: '#FFFFFF',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#4F46E5';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
              }}
            />
            {errors.email && (
              <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>{errors.email.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                {...register('password', { 
                  required: 'Password is required' 
                })}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  paddingRight: '44px',
                  borderRadius: '12px',
                  border: '1px solid #D1D5DB',
                  background: '#FFFFFF',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#4F46E5';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D1D5DB';
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#6B7280',
                  cursor: 'pointer'
                }}
              >
                <Icon name={showPassword ? 'eye' : 'eye'} size={16} />
              </button>
            </div>
            {errors.password && (
              <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>{errors.password.message}</p>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#6B7280' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: '#4F46E5', width: '14px', height: '14px' }}
              />
              Remember me
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{
                fontSize: '14px',
                fontWeight: '700',
                transition: 'colors 0.2s',
                color: '#4F46E5',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Forgot?
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={loading}
            style={{ 
              width: '100%', 
              padding: '12px',
              background: 'linear-gradient(135deg, #0F172A, #1E293B)',
              border: 'none'
            }}
          >
            Sign In
          </Button>
        </form>

        {/* Register Link */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ color: '#6B7280', fontSize: '13px' }}>
            Need access?{' '}
            <button
              onClick={() => navigate('/contact')}
              style={{
                fontWeight: '700',
                transition: 'colors 0.2s',
                color: '#4F46E5',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Contact Support
            </button>
          </p>
        </div>

        {/* Back Button */}
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <button
            onClick={handleBack}
            style={{
              fontSize: '14px',
              fontWeight: '500',
              transition: 'colors 0.2s',
              color: '#6B7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
